const BULLET_PREFIX_RE = /^\s*(?:[•·●▪◦‣*]|-(?=\s)|o(?=\s))\s*/i;
const INLINE_BULLET_RE = /\s+(?=[•●▪◦‣]\s+)/g;

export interface EducationDescriptionParts {
  narrative: string;
  bullets: string[];
}

export interface ComposeEducationDescriptionInput {
  description?: string | null;
  details?: Array<string | null | undefined> | null;
  grade?: string | null;
  /** Text shown before the narrative in clients without a field-of-study slot. */
  leadingText?: string | null;
}

function cleanLine(value: string | null | undefined) {
  return (value ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanBullet(value: string | null | undefined) {
  return cleanLine(value).replace(BULLET_PREFIX_RE, "").trim();
}

function comparable(value: string | null | undefined) {
  return cleanLine(value)
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

function dedupe(values: string[]) {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = comparable(value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Parse the stable plain-text representation stored in Education.description.
 * Narrative remains prose; explicit source bullets remain separate list items.
 */
export function splitEducationDescription(
  value: string | null | undefined,
): EducationDescriptionParts {
  const source = (value ?? "")
    .replace(/\r\n?/g, "\n")
    // PDF/AI extraction can linearize several glyph bullets onto one line.
    .replace(INLINE_BULLET_RE, "\n");
  const narrative: string[] = [];
  const bullets: string[] = [];
  let activeBullet = -1;

  for (const rawLine of source.split("\n")) {
    const line = cleanLine(rawLine);
    if (!line) continue;

    if (BULLET_PREFIX_RE.test(line)) {
      const bullet = cleanBullet(line);
      if (bullet) {
        bullets.push(bullet);
        activeBullet = bullets.length - 1;
      }
      continue;
    }

    // Wrapped text after a bullet belongs to that bullet, not to the prose
    // above it. This matters for narrow PDF columns and OCR line wrapping.
    if (activeBullet >= 0) {
      bullets[activeBullet] = `${bullets[activeBullet]} ${line}`.trim();
    } else {
      narrative.push(line);
    }
  }

  return {
    narrative: narrative.join(" ").trim(),
    bullets: dedupe(bullets),
  };
}

function bulletRepresentsGrade(bullet: string, grade: string) {
  const bulletKey = comparable(bullet);
  const gradeKey = comparable(grade);
  return Boolean(
    gradeKey &&
      (bulletKey.includes(gradeKey) ||
        /^(?:gpa|cgpa|grade|average|mark|marks|score|classification)\b/i.test(
          bullet,
        )),
  );
}

/**
 * Compose Education.description without flattening source bullets. Existing
 * records without bullets keep their legacy compact " | " presentation.
 */
export function composeEducationDescription({
  description,
  details,
  grade,
  leadingText,
}: ComposeEducationDescriptionInput): string {
  const parsed = splitEducationDescription(description);
  const explicitDetails = (details ?? []).map(cleanBullet).filter(Boolean);
  const bullets = dedupe([...parsed.bullets, ...explicitDetails]);
  const cleanGrade = cleanLine(grade);

  if (
    bullets.length > 0 &&
    cleanGrade &&
    !bullets.some((bullet) => bulletRepresentsGrade(bullet, cleanGrade))
  ) {
    bullets.unshift(cleanGrade);
  }

  const prose = dedupe(
    [cleanLine(leadingText), parsed.narrative].filter(Boolean),
  );
  if (bullets.length > 0) {
    return [
      ...(prose.length ? [prose.join(" | ")] : []),
      ...bullets.map((item) => `• ${item}`),
    ].join("\n");
  }

  return dedupe([cleanLine(leadingText), cleanGrade, parsed.narrative])
    .join(" | ")
    .trim();
}
